import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { adminApi, api } from "@/lib/api";
import { ImageField } from "./ImageField";

const EMPTY = { name: "", description: "", hero_image: "", sort_order: 0 };

export default function AdminCategories() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/categories").then((r) => setRows(r.data)).catch(() => toast.error("Failed to load categories"));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { id, ...body } = editing;
    body.sort_order = Number(body.sort_order) || 0;
    try {
      if (id) {
        await adminApi.put(`/admin/categories/${id}`, body);
        toast.success("Category updated");
      } else {
        await adminApi.post("/admin/categories", body);
        toast.success("Category created");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete category "${row.name}"?`)) return;
    try {
      await adminApi.delete(`/admin/categories/${row.id}`);
      toast.success("Category deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const set = (k) => (v) => setEditing((s) => ({ ...s, [k]: v }));

  return (
    <div data-testid="admin-categories-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-neutral-900">Categories</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Portfolio categories and their ordering.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="bg-[#4B0F1E] hover:bg-[#3a0c17]" data-testid="categories-add-btn">
          <Plus size={15} className="mr-2" /> Add category
        </Button>
      </div>

      <div className="mt-5 bg-white border border-neutral-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Sort</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-testid={`category-row-${c.id}`}>
                <TableCell className="font-medium text-neutral-900">{c.name}</TableCell>
                <TableCell className="text-neutral-500 max-w-md truncate">{c.description}</TableCell>
                <TableCell className="text-neutral-500">{c.sort_order}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setEditing({ ...EMPTY, ...c })} data-testid={`category-edit-${c.id}`}>
                    <Pencil size={15} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(c)} data-testid={`category-delete-${c.id}`}>
                    <Trash2 size={15} className="text-red-600" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="category-dialog">
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <DialogHeader>
                <DialogTitle>{editing.id ? "Edit category" : "Add category"}</DialogTitle>
                <DialogDescription>Portfolio category shown on the public website.</DialogDescription>
              </DialogHeader>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Name *</Label>
                <Input required value={editing.name} onChange={(e) => set("name")(e.target.value)} data-testid="category-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Description</Label>
                <Textarea rows={3} value={editing.description} onChange={(e) => set("description")(e.target.value)} data-testid="category-description-input" />
              </div>
              <ImageField label="Hero image" value={editing.hero_image} onChange={set("hero_image")} testId="category-hero-upload" />
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-neutral-500">Sort order</Label>
                <Input type="number" value={editing.sort_order} onChange={(e) => set("sort_order")(e.target.value)} data-testid="category-sort-input" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy} className="bg-[#4B0F1E] hover:bg-[#3a0c17]" data-testid="category-save-btn">
                  {busy ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
